import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Select, InputNumber, Spin, Empty, Input } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiVentasInstance, authHeaderVentas } from "../../redux/actions/ventas/ventas";

import DevolucionesVenta from "../../components/devoluciones/DevolucionesVenta";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
  DELETE: { label: "Cancelacion", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function VentaDetallePage() {
  const { idVenta } = useParams();

  const [venta, setVenta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("datos");
  const [cancelando, setCancelando] = useState(false);

  const [formasPago, setFormasPago] = useState([]);
  const [addAbonoOpen, setAddAbonoOpen] = useState(false);
  const [savingAbono, setSavingAbono] = useState(false);
  const [nuevoAbono, setNuevoAbono] = useState({});

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchVenta = useCallback(() => {
    setLoading(true);
    apiVentasInstance.get(`/ventas/${idVenta}`, { headers: authHeaderVentas() })
      .then((res) => setVenta(res.data))
      .catch(() => toast.error("No se pudo cargar la venta"))
      .finally(() => setLoading(false));
  }, [idVenta]);

  useEffect(() => { fetchVenta(); }, [fetchVenta]);

  useEffect(() => {
    apiVentasInstance.get("/formas-pago", { headers: authHeaderVentas() })
      .then((r) => setFormasPago((r.data?.items ?? []).map((f) => ({ label: f.nombre, value: f.id_forma_pago }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiVentasInstance.get(`/ventas/${idVenta}/actividad`, { headers: authHeaderVentas() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idVenta]);

  const handleAddAbono = async () => {
    if (!nuevoAbono.id_forma_pago || nuevoAbono.monto == null) {
      toast.warning("Selecciona la forma de pago e ingresa el monto");
      return;
    }
    setSavingAbono(true);
    try {
      await apiVentasInstance.post(`/ventas/${idVenta}/abonos`, nuevoAbono, { headers: authHeaderVentas() });
      toast.success("Abono registrado");
      setAddAbonoOpen(false);
      setNuevoAbono({});
      fetchVenta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar el abono");
    } finally {
      setSavingAbono(false);
    }
  };

  const handleCancelar = () => {
    let motivo = "";
    Modal.confirm({
      title: "Cancelar esta venta?",
      content: (
        <div>
          <p>Esta accion devuelve la existencia descontada por esta venta. No se puede deshacer.</p>
          <Input.TextArea rows={2} placeholder="Motivo de cancelacion" onChange={(e) => { motivo = e.target.value; }} />
        </div>
      ),
      okText: "Cancelar venta",
      okButtonProps: { danger: true },
      cancelText: "Volver",
      onOk: async () => {
        if (!motivo.trim()) {
          toast.warning("Escribe un motivo de cancelacion");
          return Promise.reject();
        }
        setCancelando(true);
        try {
          await apiVentasInstance.post(`/ventas/${idVenta}/cancelar`, { motivo }, { headers: authHeaderVentas() });
          toast.success("Venta cancelada");
          fetchVenta();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo cancelar");
        } finally {
          setCancelando(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!venta) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Venta no encontrada" />
      </div>
    );
  }

  const totalPagado = (venta.pagos || []).reduce((a, p) => a + parseFloat(p.monto || 0), 0);
  const totalAbonado = (venta.abonos || []).reduce((a, ab) => a + parseFloat(ab.monto || 0), 0);

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">VENTA #{venta.id_venta} - {(venta.cliente_nombre || "PUBLICO GENERAL").toUpperCase()}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-calendar" /> Registrada el {fmtFechaCorta(venta.datetime)}</span>
                {venta.cajero_nombre && <span className="cd-meta-item"><i className="fa-solid fa-user" /> {venta.cajero_nombre}</span>}
              </div>
            </div>
            <div className="cd-header-actions">
              {!venta.cancelada && (
                <Button icon={<i className="fa-solid fa-ban" />} className="cd-btn-delete" loading={cancelando} onClick={handleCancelar}>
                  Cancelar venta
                </Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "productos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("productos")}>
              <i className="fa-solid fa-list" /> Productos y servicios ({(venta.detalle || []).length})
            </button>
            <button className={`cd-tab-btn ${activeTab === "pagos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("pagos")}>
              <i className="fa-solid fa-money-bill-wave" /> Pagos
            </button>
            <button className={`cd-tab-btn ${activeTab === "devoluciones" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("devoluciones")}>
              <i className="fa-solid fa-rotate-left" /> Devoluciones
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {venta.cancelada === 1 && (
          <div className="cd-card" style={{ background: "#fee2e2", border: "1px solid #fecaca", marginBottom: 16 }}>
            <strong style={{ color: "#b91c1c" }}>Esta venta fue cancelada.</strong>
            <div style={{ marginTop: 6, color: "#7f1d1d" }}>{venta.motivo_cancelacion}</div>
            <div style={{ marginTop: 4, fontSize: 12, color: "#991b1b" }}>
              Por {venta.cancelado_por || "—"} el {fmtDatetime(venta.fecha_cancelacion)}
            </div>
          </div>
        )}

        {activeTab === "datos" && (
          <div className="cd-bento-grid" style={{ gridTemplateColumns: "2fr 1fr" }}>
            <div className="cd-card cd-card-client">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-receipt"></i></div>
                <h2 className="cd-card-title">Informacion de la venta</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                <div><span className="cd-field-label">Cliente</span><span className="cd-field-value">{venta.cliente_nombre || "Publico general"}</span></div>
                <div><span className="cd-field-label">Cajero</span><span className="cd-field-value">{venta.cajero_nombre || "—"}</span></div>
                <div><span className="cd-field-label">Es a credito</span><span className="cd-field-value">{venta.es_credito ? "Si" : "No"}</span></div>
                <div><span className="cd-field-label">Estado de facturacion</span><span className="cd-field-value">{venta.estado_facturacion}</span></div>
                <div><span className="cd-field-label">Folio fiscal</span><span className="cd-field-value">{venta.folio_fiscal || "—"}</span></div>
                <div><span className="cd-field-label">Fecha de registro</span><span className="cd-field-value">{fmtFechaCorta(venta.datetime)}</span></div>
                {venta.nota_domicilio && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <span className="cd-field-label">Nota</span>
                    <span className="cd-field-value" style={{ fontWeight: 400, color: "#595c5e" }}>{venta.nota_domicilio}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="cd-card">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-coins"></i></div>
                <h2 className="cd-card-title">Totales</h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><span className="cd-field-label">Total</span><span className="cd-field-value" style={{ fontSize: 18 }}>{money(venta.total)}</span></div>
                <div><span className="cd-field-label">Pagado de contado</span><span className="cd-field-value" style={{ color: "#15803d" }}>{money(totalPagado)}</span></div>
                {Boolean(venta.es_credito) && (
                  <>
                    <div><span className="cd-field-label">Abonado</span><span className="cd-field-value" style={{ color: "#15803d" }}>{money(totalAbonado)}</span></div>
                    <div><span className="cd-field-label">Saldo pendiente</span><span className="cd-field-value" style={{ color: parseFloat(venta.saldo_pendiente || 0) > 0 ? "#b45309" : "#15803d" }}>{money(venta.saldo_pendiente)}</span></div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "productos" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-list"></i></div>
              <h2 className="cd-card-title">Productos y servicios vendidos</h2>
            </div>
            {(venta.detalle || []).length === 0 ? (
              <Empty description="Sin lineas" />
            ) : (
              <div className="cd-pagos-list">
                {venta.detalle.map((d) => (
                  <div key={d.id_venta_detalle} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Item</span><div className="cd-field-value">{d.item_nombre || d.descripcion}</div></div>
                    <div><span className="cd-field-label">Tipo</span><div className="cd-field-value" style={{ textTransform: "capitalize" }}>{d.tipo_linea}</div></div>
                    <div><span className="cd-field-label">Cantidad</span><div className="cd-field-value">{d.cantidad}</div></div>
                    <div><span className="cd-field-label">Precio</span><div className="cd-field-value">{money(d.precio_cobrado)}</div></div>
                    <div><span className="cd-field-label">Importe</span><div className="cd-field-value">{money((parseFloat(d.cantidad) * parseFloat(d.precio_cobrado)).toFixed(2))}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "pagos" && (
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-money-bill-wave"></i></div>
                <h2 className="cd-card-title">Pagos</h2>
              </div>
              {Boolean(venta.es_credito) && !venta.cancelada && parseFloat(venta.saldo_pendiente || 0) > 0 && (
                <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => setAddAbonoOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Registrar abono
                </Button>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <div className="cc-section-header" style={{ marginBottom: 8 }}>Pagos de contado</div>
              {(venta.pagos || []).length === 0 ? (
                <Empty description="Sin pagos de contado" />
              ) : (
                <div className="cd-pagos-list">
                  {venta.pagos.map((p) => (
                    <div key={p.id_venta_pago} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                      <div><span className="cd-field-label">Forma de pago</span><div className="cd-field-value">{p.forma_pago_nombre}</div></div>
                      <div><span className="cd-field-label">Monto</span><div className="cd-field-value">{money(p.monto)}</div></div>
                      <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(p.datetime)}</div></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {Boolean(venta.es_credito) && (
              <div>
                <div className="cc-section-header" style={{ marginBottom: 8 }}>Abonos a credito</div>
                {(venta.abonos || []).length === 0 ? (
                  <Empty description="Todavia no hay abonos registrados" />
                ) : (
                  <div className="cd-pagos-list">
                    {venta.abonos.map((a) => (
                      <div key={a.id_abono} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                        <div><span className="cd-field-label">Forma de pago</span><div className="cd-field-value">{a.forma_pago_nombre}</div></div>
                        <div><span className="cd-field-label">Monto</span><div className="cd-field-value">{money(a.monto)}</div></div>
                        <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(a.datetime)}</div></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "devoluciones" && <DevolucionesVenta venta={venta} />}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={addAbonoOpen}
        onCancel={() => setAddAbonoOpen(false)}
        onOk={handleAddAbono}
        okText="Registrar abono"
        cancelText="Cancelar"
        title="Registrar abono a credito"
        confirmLoading={savingAbono}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Select placeholder="Forma de pago" options={formasPago} value={nuevoAbono.id_forma_pago} onChange={(v) => setNuevoAbono((f) => ({ ...f, id_forma_pago: v }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Monto" min={0} precision={2} value={nuevoAbono.monto} onChange={(v) => setNuevoAbono((f) => ({ ...f, monto: v }))} />
        </div>
      </Modal>
    </div>
  );
}
